import { fetcher } from '#lib/backendFetcher';

const SNIPPET_ID = 'ze-snippet';
const SNIPPET_SRC = 'https://static.zdassets.com/ekr/snippet.js?key=cea87531-9518-46d4-a114-c99ff59b3e87';

/**
 * Fetches a signed Zendesk messaging identity token for the current user.
 * The backend derives the identity from the session — nothing is sent from here.
 * @returns {Promise<string>} The signed JWT
 */
export async function fetchZendeskToken() {
	const { token } = await fetcher('/api/v1/platform/zendesk/token', { method: 'POST' });
	return token;
}

/**
 * Whether the Zendesk widget script has finished loading.
 * @returns {boolean}
 */
export function isZendeskReady() {
	return typeof window.zE === 'function';
}

/**
 * Injects the widget script. Idempotent — the script is only ever added once per page load.
 * Only called for logged-in users, so anonymous visitors never load Zendesk at all.
 * @returns {void}
 */
export function loadZendesk() {
	if (document.getElementById(SNIPPET_ID)) return;

	const script = document.createElement('script');
	script.id = SNIPPET_ID;
	script.src = SNIPPET_SRC;
	script.async = true;
	document.head.appendChild(script);
}

/**
 * Runs `callback` once the widget is ready to take commands.
 *
 * The snippet loads async, so `window.zE` may not exist yet right after `loadZendesk()`. Once it
 * does, Zendesk's own `zE(fn)` form defers the call until the widget finished initialising.
 *
 * @param {() => void} callback
 * @returns {() => void} Cancel function detaching the pending load listener
 */
export function whenZendeskReady(callback) {
	if (isZendeskReady()) {
		window.zE(callback);
		return () => {};
	}

	const snippet = document.getElementById(SNIPPET_ID);
	if (!snippet) return () => {};

	const onLoad = () => {
		if (isZendeskReady()) window.zE(callback);
	};

	snippet.addEventListener('load', onLoad, { once: true });
	return () => snippet.removeEventListener('load', onLoad);
}

/**
 * Ends the authenticated Zendesk session and clears its local chat history.
 * Safe to call when the widget never loaded.
 * @returns {void}
 */
export function logoutZendeskUser() {
	if (!isZendeskReady()) return;
	window.zE('messenger', 'logoutUser');
}
